
import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = "https://api.getfitindia.in/api";

const AdminCourtOwners = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const res = await axios.get(`${API_BASE}/admin/courtowners/court-owners`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        });

        // Debug once if needed
        console.log("ADMIN COURT OWNERS →", res.data.owners?.[0]);

        setOwners(res.data.owners || []);
      } catch (err) {
        console.error("Fetch owners error:", err.response?.data || err);
        setErrorMsg("Failed to fetch court owners. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchOwners();
  }, [token]);

  const toggleExpand = (ownerId) => {
    setExpandedRows((prev) =>
      prev.includes(ownerId)
        ? prev.filter((id) => id !== ownerId)
        : [...prev, ownerId]
    );
  };

  const toggleBlock = async (ownerId) => {
    try {
      const res = await fetch(
        `${API_BASE}/admin/courtowners/court-owners/${ownerId}/block`,
        {
          method: "PATCH",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
        }
      );

      const data = await res.json();
      if (data.error) {
        return alert(data.error);
      }

      alert(data.message || "Updated.");
      setOwners((prev) =>
        prev.map((o) =>
          o._id === ownerId ? { ...o, blocked: data.blocked } : o
        )
      );
    } catch (err) {
      console.error("Block/unblock error:", err);
      alert("Failed to block/unblock owner.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB] flex flex-col">
      {/* Top Header Bar (like My Users) */}
      <div className="bg-[#0f2250] text-white w-full">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center ">
          <h1 className="text-lg font-semibold ms-12 sm:ms-0 mt-1">
            Court Owners
          </h1>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 px-3 sm:px-5 lg:px-10 py-4 overflow-y-auto">
        <div className="max-w-[100rem] mx-auto">
          {/* Header Row */}
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm sm:text-base font-semibold text-[#0C295F] ml-[33px]">
              All registered court owners
            </span>

            <span className="text-xs sm:text-sm text-gray-500">
              Total: {owners.length}
            </span>
          </div>

          {/* Table Container */}
          <div className="rounded-xl p-3 sm:p-4 shadow-[0_0_6px_rgba(0,0,0,0.2)] bg-white overflow-x-auto">
            {loading && (
              <p className="text-sm text-gray-500">Loading court owners...</p>
            )}

            {errorMsg && (
              <p className="text-sm text-red-500 mb-3">{errorMsg}</p>
            )}

            {!loading && !errorMsg && owners.length === 0 && (
              <p className="text-sm text-gray-600">
                No court owners found.
              </p>
            )}

            {!loading && !errorMsg && owners.length > 0 && (
              <table className="min-w-[900px] w-full text-sm">
                <thead className="bg-[#F3F6FB] text-[#0C295F]">
                  <tr>
                    <th className="px-3 py-2 border-b text-left">#</th>
                    <th className="px-3 py-2 border-b text-left whitespace-nowrap">
                      Owner Name
                    </th>
                    <th className="px-3 py-2 border-b text-left whitespace-nowrap">
                      Mobile
                    </th>
                    <th className="px-3 py-2 border-b text-left">
                      Address
                    </th>
                    <th className="px-3 py-2 border-b text-left">
                      Status
                    </th>
                    <th className="px-3 py-2 border-b text-left">
                      Blocked
                    </th>
                    <th className="px-3 py-2 border-b text-left whitespace-nowrap">
                      Created
                    </th>
                    <th className="px-3 py-2 border-b text-left whitespace-nowrap">
                      Venues
                    </th>
                    <th className="px-3 py-2 border-b text-left whitespace-nowrap">
                      Logins
                    </th>
                    <th className="px-3 py-2 border-b text-left">
                      Block / Unblock
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {owners.map((o, idx) => (
                    <React.Fragment key={o._id || idx}>
                      {/* MAIN ROW */}
                      <tr className="odd:bg-white even:bg-[#F9FBFF] hover:bg-[#EEF3FF] transition">
                        <td className="px-3 py-2 border-b">
                          {idx + 1}
                        </td>

                        <td className="px-3 py-2 border-b break-words">
                          {o.name || "N/A"}
                        </td>

                        <td className="px-3 py-2 border-b truncate max-w-[120px]">
                          {o.mobile}
                        </td>

                        <td className="px-3 py-2 border-b break-words">
                          {o.address || o.city || "N/A"}
                        </td>

                        <td className="px-3 py-2 border-b">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              o.status === "approved"
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : "bg-yellow-50 text-yellow-700 border border-yellow-200"
                            }`}
                          >
                            {o.status || "pending"}
                          </span>
                        </td>

                        <td className="px-3 py-2 border-b">
                          {o.blocked ? (
                            <span className="text-red-600 font-semibold text-xs">
                              Yes
                            </span>
                          ) : (
                            <span className="text-green-600 font-semibold text-xs">
                              No
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-2 border-b whitespace-nowrap">
                          {o.createdAt?.split("T")[0] || "N/A"}
                        </td>

                        <td className="px-3 py-2 border-b">
                          {o.venuesCount ?? 0}
                        </td>

                        <td className="px-3 py-2 border-b">
                          <button
                            className="px-3 py-1 rounded-full text-xs font-semibold bg-[#0f2250] hover:bg-[#071833] text-white transition"
                            onClick={() => toggleExpand(o._id)}
                          >
                            {expandedRows.includes(o._id)
                              ? "Hide"
                              : "View"}
                          </button>
                        </td>

                        <td className="px-3 py-2 border-b">
                          <button
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
                              o.blocked
                                ? "bg-green-600 hover:bg-green-700 text-white"
                                : "bg-red-600 hover:bg-red-700 text-white"
                            }`}
                            onClick={() => toggleBlock(o._id)}
                          >
                            {o.blocked ? "Unblock" : "Block"}
                          </button>
                        </td>
                      </tr>

                      {/* EXPANDED ROW: Additional Login Users */}
                      {expandedRows.includes(o._id) && (
                        <tr className="bg-[#FAFBFF]">
                          <td colSpan="10" className="px-6 py-4 border-b">
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center justify-between gap-2">
                                <h3 className="font-semibold text-gray-800 text-sm">
                                  Additional Login Users
                                </h3>
                                <span className="text-[11px] text-gray-500">
                                  Count:{" "}
                                  <span className="font-semibold">
                                    {o.staff ? o.staff.length : 0}
                                  </span>
                                </span>
                              </div>

                              {!o.staff || o.staff.length === 0 ? (
                                <p className="text-xs sm:text-sm text-gray-500">
                                  No additional login users for this owner.
                                </p>
                              ) : (
                                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                  {o.staff.map((s) => (
                                    <div
                                      key={s._id}
                                      className="border border-gray-200 rounded-lg p-3 bg-white shadow-sm text-[11px] sm:text-xs"
                                    >
                                      <div className="font-semibold text-gray-800 mb-1">
                                        {s.name}
                                      </div>
                                      <div className="space-y-0.5 text-gray-600">
                                        <div>
                                          <span className="font-semibold">
                                            Mobile:
                                          </span>{" "}
                                          {s.mobile}
                                        </div>
                                        <div>
                                          <span className="font-semibold">
                                            Email:
                                          </span>{" "}
                                          {s.email || "-"}
                                        </div>
                                        <div>
                                          <span className="font-semibold">
                                            Business:
                                          </span>{" "}
                                          {s.businessName || "-"}
                                        </div>
                                        <div>
                                          <span className="font-semibold">
                                            Address:
                                          </span>{" "}
                                          {s.address || "-"}
                                        </div>
                                        <div>
                                          <span className="font-semibold">
                                            Role:
                                          </span>{" "}
                                          {s.role || "staff"}
                                        </div>
                                        <div>
                                          <span className="font-semibold">
                                            Created:
                                          </span>{" "}
                                          {s.createdAt?.split("T")[0] || "-"}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCourtOwners;
